import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import { I_Category, I_CheckoutCartItem } from 'app/pages/Cart/slice/types';
import { CheckoutDetail, Orderdate } from 'app/pages/Checkout/slice/types';
import {
  createCheckoutPaymentApi,
  getCheckoutDetailOrderApi,
} from 'app/services/checkoutApi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { I_Park, objectType } from 'types';
import { TYPE_LOCAL_STORAGE } from 'utils/constants';
import { getSessionItem } from 'utils/localStorage';
import { useCheckoutSlice } from '../slice';
import { selectListPaymentMethod } from '../slice/selectors';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import moment from 'moment';
import { isTimeValid } from 'utils/time';
import { isNil } from 'lodash';
import { windowOpenLink } from 'utils/helper';

interface Props {
  paymentMethodActive?: number;
  onTriggerSubmit?: () => void;
}

export type GroupDatas = {
  park: I_Park;
  catalogs: Record<
    string,
    {
      catalog: any;
      items: I_CheckoutCartItem[];
    }
  >;
  show?: any;
};

export const MATERRIAL_SHOW_TYPE = '9004'; //Vé show
export const MATERRIAL_PARK_TYPE = '9005'; //Vé công viên
export const MATERRIAL_EVOUCHER_TYPE = '9006'; //Evoucher
export const MATERRIAL_COMBO_TYPE = '9008'; //Combo

const groupItems = (
  items: I_CheckoutCartItem[],
): Record<
  string,
  {
    park: I_Park;
    catalogs: Record<string, { catalog: any; items: I_CheckoutCartItem[] }>;
  }
> => {
  return items.reduce((groupedItems, item) => {
    const parkKey = `${item.park.id}`;
    const catalogKey = `${item.catalog.id}`;

    if (!groupedItems[parkKey]) {
      groupedItems[parkKey] = { park: item.park, catalogs: {} };
    }

    if (!groupedItems[parkKey].catalogs[catalogKey]) {
      groupedItems[parkKey].catalogs[catalogKey] = {
        catalog: item.catalog,
        items: [],
      };
    }

    groupedItems[parkKey].catalogs[catalogKey].items.push(item);

    return groupedItems;
  }, {} as Record<string, { park: I_Park; catalogs: Record<string, { catalog: any; items: I_CheckoutCartItem[] }> }>);
};

export const useCheckout = () => {
  useCheckoutSlice();
  const [listCart, setListCart] = useState<I_CheckoutCartItem[]>([]);
  const [vouchers, setVouchers] = useState<Partial<I_CheckoutCartItem>[]>([]);
  const [groupCart, setGroupCart] = useState<GroupDatas[]>([]);
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const translations = useSelector(selectTranslations);
  const { id }: { id: string } = useParams();

  const [loading, setLoading] = useState(false);
  const [enableConfirm, setEnableConfirm] = useState(false);
  const [idOrder, setIdOrder] = useState<number>();
  const [payment, setPayment] =
    useState<{ id: number; name: string; code?: string }>();
  const orderCheckout = useRef<CheckoutDetail>();
  const [bankInfo, setBankInfo] = useState<any>();
  const [orderState, setOrderState] = useState<any>();
  const [expiredHours, setExpiredHours] = useState<any>();
  const [expiredAt, setExpiredAt] = useState<any>();
  const [startTime, setStartTime] = useState<any>();
  const [order, setOrder] = useState<any>();
  const [isValidPaymentTime, setIsValidPaymentTime] = useState<any>();

  const history = useHistory();

  const infoUser = useSelector(selectInfoUser);
  const listPaymentMethod = useSelector(selectListPaymentMethod);
  // const datesSelectedToCheckout = useSelector(selectDatesSelectedToCheckout);
  const INIT_FORM = useMemo(
    () => ({
      name: infoUser?.name || '',
      phone: infoUser?.phone || '',
      email: infoUser?.email || '',
    }),
    [infoUser],
  );

  const totalAmount = useMemo(
    () => listCart.reduce((result, item) => result + +item.amount, 0),
    [listCart],
  );

  const [totalPrice, setTotalPrice] = useState(0);

  const [totalPriceOriginal, setTotalPriceOriginal] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);

  const onSubmitForm = useCallback(async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('idHash', orderCheckout.current?.idHash ?? '');
      formData.append('queryCallback', '');

      const { data } = await createCheckoutPaymentApi(formData);
      if (data.paymentUrl) {
        if (payment?.code === 'ncb') {
          history.push('/ncb-payment', {
            id: orderCheckout.current?.idHash,
            qrImage: data.paymentUrl,
            bankInfo: {
              bankName: data?.ncbInfo?.account_name ?? '',
              bankAccount: data?.ncbInfo?.account_no ?? '',
            },
          });
        } else {
          window.open(data.paymentUrl, '_self');
        }
      } else {
        if (data?.paymentSuccess) {
          // sessionStorage.removeItem(TYPE_LOCAL_STORAGE.ORDER);
          windowOpenLink({
            url: `/payment-callback?vnp_TxnRef=${orderCheckout.current?.idHash}&bnPaymentType=Deposit&result_code=1`,
            target: '_self',
          });
        } else {
          if (data?.isPendingConfirm) {
            return history.push('/pending-confirm', {
              orderId: idOrder,
            });
          }
          onOpenDialogNoti({
            type: 'ERROR',
            isShow: true,
            title: translations?.['b2b.txt.payment-deposit-fail'] ?? '',
            content: data?.message ?? '',
            closable: true,
            onCloseDialog: onCloseDialogNoti,
            maskClosable: false,
          });
        }
      }
    } catch (err: any) {
      if (err?.action?.toLowerCase() === 'popup') {
        onOpenDialogNoti({
          type: 'ERROR',
          isShow: true,
          title: '',
          content: err?.message,
          buttons: [],
          maskClosable: false,
          closable: true,
          onCloseDialog: () => {
            onCloseDialogNoti();
            err?.data?.code === 'MSG070' &&
              history.replace('/account?route=tab-3');
          },
        });
      }
    } finally {
      setLoading(false);
    }
  }, [
    history,
    onCloseDialogNoti,
    onOpenDialogNoti,
    payment?.code,
    translations,
  ]);
  const getTimeRemainingLabel = (targetTime: Orderdate, startOfTime) => {
    const expiredAt = moment(
      targetTime?.datetimeDMYHIS ?? undefined,
      'HH:mm:ss DD/MM/YYYY',
    );
    const now = moment();
    const diffMsFromNow = expiredAt.valueOf() - now.valueOf();
    if (diffMsFromNow <= 0) return undefined;
    const startTime = moment(
      startOfTime?.datetimeDMYHIS ?? undefined,
      'HH:mm:ss DD/MM/YYYY',
    );
    const time = expiredAt.valueOf() - startTime.valueOf();
    // Quy đổi ra giờ (bao gồm phần lẻ)
    const diffHours = time / (1000 * 60 * 60);
    // Làm tròn 1 chữ số thập phân (ví dụ: 1.3h)
    let roundedHours = Math.round(diffHours * 10) / 10;
    if (diffHours < 3 / 60) {
      roundedHours = Number(diffHours.toFixed(2));
    }
    return roundedHours;
  };

  const getOrderDetail = useCallback(async () => {
    try {
      setLoading(true);
      const { data: detailOrder } = await getCheckoutDetailOrderApi(id);
      if (detailOrder.orderState.value === 'success') {
        history.push('/page-not-found');
      }
      orderCheckout.current = detailOrder;
      setTotalDiscount(detailOrder.totalDiscount);
      setTotalPrice(detailOrder.totalPrice);
      setTotalPriceOriginal(detailOrder.totalOriginalPrice);
      setIdOrder(detailOrder.id);
      setPayment(detailOrder.payment);
      setBankInfo(detailOrder?.businessPartner?.bankInfo);
      setOrderState(detailOrder?.orderState);
      setOrder(detailOrder);
      const expiredHours = getTimeRemainingLabel(
        detailOrder?.expiredAt,
        detailOrder?.startTime,
      );
      setExpiredHours(expiredHours);

      setExpiredAt(detailOrder.expiredAt);
      const items: I_CheckoutCartItem[] = detailOrder?.items.map(item => ({
        ...item,
        product: {
          id: item.id,
          name: item.productName,
          price: item.price,
          originalPrice: item.originalPrice,
          categories: [] as I_Category[],
          amount: `${item.amount}`,
          parks: [item.park],
          park: item.park,
          image: item.park.image,
          versionB2b: item?.product?.versionB2b,
          usedArea2Object: item?.product?.usedArea2Object,
        },
        amount: `${item.amount}`,
        date: item.date.dateDMY,
        zone: item.zone,
        show: item.show,
        price: item.price,
        originalPrice: item.originalPrice,
        performance: { ...item.performance, show: item.show },
        park: item.park,
        catalog: item.catalog,
        isGift: false,
        selectedDate: item.selectedDate?.dateDMY ?? '',
      }));
      const giftItems: I_CheckoutCartItem[] =
        detailOrder?.giftItems
          ?.filter(
            val =>
              val.materialType === MATERRIAL_SHOW_TYPE ||
              val.materialType === MATERRIAL_PARK_TYPE,
          )
          .map(item => ({
            ...item,
            product: {
              id: item.id,
              name: item.productName ?? '',
              price: item.price,
              originalPrice: item.originalPrice,
              categories: [] as I_Category[],
              amount: `${item.amount}`,
              parks: [item.park],
              park: item.park,
              image: item.park?.image ?? '',
              versionB2b: item?.product?.versionB2b,
              usedArea2Object: item?.product?.usedArea2Object,
            },
            amount: `${item.amount ?? 0}`,
            date: item.date?.dateDMY ?? '',
            zone: item.zone,
            show: item.show,
            price: item.price,
            originalPrice: item.originalPrice,
            performance: { ...item.performance, show: item.show },
            park: item.park,
            catalog: item.catalog,
            isGift: true,
            selectedDate: item.selectedDate?.dateDMY ?? '',
            higherLevelItem: item.higherLevelItem ?? '',
          })) ?? [];
      const vouchersData: Partial<I_CheckoutCartItem>[] | any =
        detailOrder?.giftItems
          ?.filter(val => val.materialType === MATERRIAL_EVOUCHER_TYPE)
          .map(item => ({
            ...item,
            product: {
              id: item.id,
              name: item.productName ?? '',
              price: item.price,
              originalPrice: item.originalPrice,
              categories: [] as I_Category[],
              amount: `${item.amount}`,
              parks: [item.park],
              park: item.park,
              image: item.park?.image ?? '',
              versionB2b: item?.product?.versionB2b,
              usedArea2Object: item?.product?.usedArea2Object,
            },
            amount: `${item.amount ?? 0}`,
            date: item.date?.dateDMY ?? '',
            zone: item.zone,
            show: item.show,
            price: item.price,
            originalPrice: item.originalPrice,
            performance: { ...item.performance, show: item.show },
            park: item.park,
            catalog: item.catalog,
            isGift: true,
          })) ?? [];
      const groupDatas = Object.values(groupItems([...items, ...giftItems]));
      setVouchers(vouchersData);
      setGroupCart(groupDatas);
      setListCart([...items, ...giftItems]);
      const hourFrom = detailOrder?.payment?.validFrom;
      const hourTo = detailOrder?.payment?.validTo;
      const isValidPayment = isTimeValid(hourFrom, hourTo);
      setIsValidPaymentTime(
        !detailOrder?.payment?.validFrom && !detailOrder?.payment?.validTo
          ? true
          : isValidPayment,
      );
      return detailOrder;
    } catch (e) {
      history.push('/page-not-found');
      throw e;
    } finally {
      setLoading(false);
    }
  }, [history, id]);

  useEffect(() => {
    const getDetail = async () => {
      try {
        setLoading(true);
        // if (!getSessionItem(TYPE_LOCAL_STORAGE.ORDER)) {
        //   history.push('/page-not-found');
        //   return;
        // }
        setEnableConfirm(true);
        // const id = JSON.parse(getSessionItem(TYPE_LOCAL_STORAGE.ORDER) ?? '');
        const { data: detailOrder } = await getCheckoutDetailOrderApi(id);
        if (detailOrder.orderState.value === 'success') {
          history.push('/page-not-found');
        }
        orderCheckout.current = detailOrder;
        setTotalDiscount(detailOrder.totalDiscount);
        setTotalPrice(detailOrder.totalPrice);
        setTotalPriceOriginal(detailOrder.totalOriginalPrice);
        setIdOrder(detailOrder.id);
        setPayment(detailOrder.payment);
        setBankInfo(detailOrder?.businessPartner?.bankInfo);
        setOrderState(detailOrder?.orderState);
        setOrder(detailOrder);
        const expiredHours = getTimeRemainingLabel(
          detailOrder?.expiredAt,
          detailOrder?.startTime,
        );
        setExpiredHours(expiredHours);

        setExpiredAt(detailOrder.expiredAt);
        setStartTime(detailOrder.startTime);
        const items: I_CheckoutCartItem[] = detailOrder?.items.map(item => ({
          ...item,
          product: {
            id: item.id,
            name: item.productName,
            price: item.price,
            originalPrice: item.originalPrice,
            categories: [] as I_Category[],
            amount: `${item.amount}`,
            parks: [item.park],
            park: item.park,
            image: item.park.image,
            versionB2b: item?.product?.versionB2b,
            usedArea2Object: item?.product?.usedArea2Object,
          },
          amount: `${item.amount}`,
          date: item.date.dateDMY,
          zone: item.zone,
          show: item.show,
          price: item.price,
          originalPrice: item.originalPrice,
          performance: { ...item.performance, show: item.show },
          park: item.park,
          catalog: item.catalog,
          isGift: false,
          selectedDate: item.selectedDate?.dateDMY ?? '',
        }));
        const giftItems: I_CheckoutCartItem[] =
          detailOrder?.giftItems
            ?.filter(
              val =>
                val.materialType === MATERRIAL_SHOW_TYPE ||
                val.materialType === MATERRIAL_PARK_TYPE,
            )
            .map(item => ({
              ...item,
              product: {
                id: item.id,
                name: item.productName ?? '',
                price: item.price,
                originalPrice: item.originalPrice,
                categories: [] as I_Category[],
                amount: `${item.amount}`,
                parks: [item.park],
                park: item.park,
                image: item.park?.image ?? '',
                versionB2b: item?.product?.versionB2b,
                usedArea2Object: item?.product?.usedArea2Object,
              },
              amount: `${item.amount ?? 0}`,
              date: item.date?.dateDMY ?? '',
              zone: item.zone,
              show: item.show,
              price: item.price,
              originalPrice: item.originalPrice,
              performance: { ...item.performance, show: item.show },
              park: item.park,
              catalog: item.catalog,
              isGift: true,
              selectedDate: item.selectedDate?.dateDMY ?? '',
              higherLevelItem: item.higherLevelItem ?? '',
            })) ?? [];
        const vouchersData: Partial<I_CheckoutCartItem>[] | any =
          detailOrder?.giftItems
            ?.filter(val => val.materialType === MATERRIAL_EVOUCHER_TYPE)
            .map(item => ({
              ...item,
              product: {
                id: item.id,
                name: item.productName ?? '',
                price: item.price,
                originalPrice: item.originalPrice,
                categories: [] as I_Category[],
                amount: `${item.amount}`,
                parks: [item.park],
                park: item.park,
                image: item.park?.image ?? '',
                versionB2b: item?.product?.versionB2b,
                usedArea2Object: item?.product?.usedArea2Object,
              },
              amount: `${item.amount ?? 0}`,
              date: item.date?.dateDMY ?? '',
              zone: item.zone,
              show: item.show,
              price: item.price,
              originalPrice: item.originalPrice,
              performance: { ...item.performance, show: item.show },
              park: item.park,
              catalog: item.catalog,
              isGift: true,
            })) ?? [];
        const groupDatas = Object.values(groupItems([...items, ...giftItems]));
        setVouchers(vouchersData);
        setGroupCart(groupDatas);
        setListCart([...items, ...giftItems]);
        const hourFrom = detailOrder?.payment?.validFrom;
        const hourTo = detailOrder?.payment?.validTo;
        const isValidPayment = isTimeValid(hourFrom, hourTo);
        setIsValidPaymentTime(
          !detailOrder?.payment?.validFrom && !detailOrder?.payment?.validTo
            ? true
            : isValidPayment,
        );
        // sessionStorage.removeItem(TYPE_LOCAL_STORAGE.ORDER);
      } catch (e) {
        history.push('/page-not-found');
        throw e;
      } finally {
        setLoading(false);
      }
    };
    getDetail();
  }, []);

  return useMemo(
    () => ({
      loading,
      INIT_FORM,
      onSubmitForm,
      listPaymentMethod,
      // listCartSelectedToCheckout,
      totalAmount,
      totalPrice,
      totalPriceOriginal,
      orderCheckout,
      enableConfirm,
      setEnableConfirm,
      totalDiscount,
      groupCart,
      infoUser,
      setLoading,
      vouchers,
      listCart,
      idOrder,
      payment,
      bankInfo,
      orderState,
      expiredHours,
      isValidPaymentTime,
      expiredAt,
      startTime,
      order,
      getOrderDetail,
    }),
    [
      loading,
      INIT_FORM,
      onSubmitForm,
      listPaymentMethod,
      // listCartSelectedToCheckout,
      totalAmount,
      totalPrice,
      totalPriceOriginal,
      orderCheckout,
      enableConfirm,
      setEnableConfirm,
      totalDiscount,
      groupCart,
      infoUser,
      setLoading,
      vouchers,
      listCart,
      idOrder,
      payment,
      bankInfo,
      orderState,
      expiredHours,
      isValidPaymentTime,
      expiredAt,
      startTime,
      order,
      getOrderDetail,
    ],
  );
};
